import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

function Privacy() {
  return (
    <div className="App-privacy-policy">
      <Seo
        title="Privacy Policy — Dreamcode"
        description="Privacy Policy for Dreamcode apps and website, including NumGen Pro, SG Bus & MRT, Rain Radar & Alerts, and Snack Snake."
        path="/privacy"
      />
      <h1>Privacy Policy</h1>
      <p>
        <strong>Last updated:</strong> 26 July 2026<br />
        This Privacy Policy describes how Dreamcode (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or
        &ldquo;our&rdquo;) handles information when you use our mobile applications and this website
        (dreamcode.app). It applies to all apps published under Dreamcode, including NumGen Pro, SG Bus
        &amp; MRT, Rain Radar &amp; Alerts, and Snack Snake (each an &ldquo;App&rdquo;).
      </p>

      <p>
        <strong>1. Summary</strong><br />
        We build utility and entertainment apps and aim to collect as little data as possible. Some
        Apps need access to location, notifications, or on-device storage to work. We do not ask you
        to create a Dreamcode account to use our Apps. Where an App uses location, it is used to power
        features such as nearby bus stops, trip planning, live journey guidance, rain maps, and
        weather alerts — not to build a profile of you. Background location and notifications are
        used only when you explicitly start an active journey in SG Bus &amp; MRT. Snack Snake does
        not use location; it may schedule a local notification when Adventure energy is full again
        if you grant notification permission.
      </p>

      <p>
        <strong>2. Information by App</strong><br />
        <strong>NumGen Pro</strong> — Generates and analyses random numbers. This App does not access
        your location. It may show advertisements (see Section 6).<br />
        <br />
        <strong>SG Bus &amp; MRT</strong> — Shows real-time Singapore bus and MRT arrivals, nearby
        stops, trip planning, and favourites. With your permission, this App accesses your device
        location to find nearby stops, centre the map, plan journeys from your current position, and
        sort favourites by distance. Location is processed on your device to provide these features.
        We do not operate user accounts for this App and do not receive your location on our servers.
        Favourite stops, routes, and journeys are stored on your device. On iPhone and iPad, you can
        optionally turn on <strong>Sync with iCloud</strong> (Settings → Sync) to keep favourites and
        display preferences in step across your Apple devices and after reinstall when signed into
        iCloud; this uses Apple&apos;s iCloud Key-Value storage, not Dreamcode servers. On Android,
        favourites may restore when you reinstall if Google backup is enabled on your device.<br />
        <br />
        If you tap <strong>Start</strong> on a planned route or saved favourite, the App can track
        that journey in real time. While a journey is active, the App may use background location and
        send notifications to alert you when your bus is approaching, when to alight or change, and
        when you have arrived. On iPhone, trip status may appear as a Live Activity on the Lock
        Screen and in the Dynamic Island. On Android, a floating trip indicator may appear over other
        apps if you grant <strong>Display over other apps</strong> permission. Journey tracking stops
        when the trip ends or you quit the App.<br />
        <br />
        Optional home screen widgets (on iOS and Android) show live arrivals for a favourite stop you
        choose. Widget data is read from information stored on your device and refreshed in the
        background so times stay up to date. This App may show advertisements (see Section 6).<br />
        <br />
        <strong>Rain Radar &amp; Alerts</strong> — Shows live rain and cloud radar on a map and can
        send push alerts for saved places. With your permission, this App accesses your location to
        centre the map, show conditions around you, and optionally alert you when rain is expected
        near your current position — including in the background if you enable that option. Saved
        places (such as home or work) and alert preferences are stored on your device. Push
        notifications require a device token issued by Apple; we use that token only to deliver
        alerts you have asked for. No sign-up or password is required. This App may show
        advertisements and use analytics related to ad delivery (see Sections 6 and 8).<br />
        <br />
        <strong>Snack Snake</strong> — A casual arcade snake game with Adventure and Classic modes.
        This App does not access your location or send marketing notifications. Game progress
        (unlocks, stars, scores, energy, store inventory, and settings) is stored on your device.
        With notification permission, the App may schedule a local reminder when Adventure energy
        (lives) will be full again — this is processed on your device and is not a push notification
        from Dreamcode servers. Optional platform game services — Google Play Games on Android and
        Apple Game Center on iOS — may be used for leaderboards and to restore Classic high scores.
        Adventure progress, energy, and purchased store items may also sync through the
        platform&apos;s cloud save (Play Games Saved Games on Android; iCloud Documents on iOS) when
        you are signed into the relevant game or iCloud account. That cloud data is handled by Apple
        or Google under their privacy policies, not by Dreamcode servers, and does not transfer
        between Android and iOS. This App may show rewarded and interstitial advertisements (see
        Section 6) and offers optional in-app purchases to remove ads, refill or boost Adventure
        energy, or buy consumable gameplay items.
      </p>

      <p>
        <strong>3. Location Data</strong><br />
        Location is requested only when a feature needs it, and you can deny or revoke permission in
        your device settings at any time. If you deny location access, features that depend on it
        (nearby stops, trip planning from your position, live journey guidance, current-location rain
        alerts, and similar) will not work fully, but other parts of the App may still be available.<br />
        <br />
        For SG Bus &amp; MRT, foreground location stays on your device and is not sent to Dreamcode
        servers. Background location is used only while you have an active journey running (after you
        tap <strong>Start</strong>) and only to provide journey alerts and on-screen trip status; it
        is not used for advertising or analytics. On Android, a foreground service may run during an
        active journey so tracking can continue when the App is in the background.<br />
        <br />
        For Rain Radar &amp; Alerts, location may be used on your device and, where needed to
        deliver alerts you enable, processed to determine whether rain is expected near a saved place
        or your current position. We do not sell your location data.
      </p>

      <p>
        <strong>4. Data Stored on Your Device and Sync</strong><br />
        Several Apps save preferences and content on your phone or tablet — for example, favourite
        bus stops, saved train routes, planned journeys, widget configurations, bookmarked map
        locations, alert settings, and for Snack Snake game progress, store inventory, audio
        preferences, and purchase flags. Home screen widgets read from on-device data where
        applicable.<br />
        <br />
        For SG Bus &amp; MRT, you can optionally sync favourites and display settings (such as app
        theme) across devices: on iOS via iCloud when <strong>Sync with iCloud</strong> is on, and on
        Android via Google&apos;s device backup when that is enabled in your system settings. Synced
        data is limited to favourites and preferences — not your location, active journey state, or
        advertising choices.<br />
        <br />
        For Snack Snake, optional cloud save restores Adventure progress, energy, and store inventory
        via Play Games Saved Games or iCloud Documents when you use the platform game account;
        leaderboards may show your display name and scores through Google Play Games or Apple Game
        Center. We do not operate Dreamcode login accounts or host this data on Dreamcode servers;
        Apple and Google handle cloud and game-service copies under their own privacy policies. Other
        data remains on your device unless you delete the App, remove a widget, or clear its data.
      </p>

      <p>
        <strong>5. Notifications</strong><br />
        Some Apps may send push or local notifications when you opt in. In SG Bus &amp; MRT,
        notifications are used during an active journey to remind you about boarding, transfers, and
        arrival — only after you start a trip and grant notification permission. Rain Radar &amp;
        Alerts uses notifications for weather alerts you configure. Snack Snake may schedule a local
        notification when Adventure energy is about to be full again, after you grant notification
        permission; these reminders are not used for marketing. You can turn notifications off at any
        time in your device Settings. We do not use notifications for marketing.
      </p>

      <p>
        <strong>6. Advertising</strong><br />
        Some Apps display advertisements provided by Google (AdMob). Google and its partners may
        collect identifiers and usage data to show and measure ads, including data that may be used
        for tracking across apps and websites owned by other companies, as described in Google&apos;s
        privacy policy. We do not control data collected by Google for advertising purposes. You can
        limit ad personalisation through your device settings and through{' '}
        <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
          Google&apos;s Ads Settings
        </a>
        . Where required, Snack Snake shows a consent form (Google&apos;s User Messaging Platform)
        before ads initialise, and a <strong>Privacy choices</strong> option may appear in game
        settings. Snack Snake does not request App Tracking Transparency on iOS, so IDFA-based
        tracking and personalisation are unavailable there. You can remove banner ads in SG Bus
        &amp; MRT and Rain Radar &amp; Alerts, and remove interstitial ads in Snack Snake, via
        optional in-app purchases. Snack Snake may also offer a rewarded ad to restore Adventure
        energy, and optional purchases for energy boosts or consumable gameplay items; watching ads
        and buying items are optional.
      </p>

      <p>
        <strong>7. Third-Party Services</strong><br />
        Our Apps rely on third-party services to function. For example, SG Bus &amp; MRT uses
        official Singapore open transport data (LTA DataMall) and map services; Rain Radar &amp;
        Alerts uses weather and radar data providers and map services; Snack Snake uses Google Play
        Games Services and Apple Game Center for leaderboards and optional cloud progress, and Apple
        or Google billing for in-app purchases. These services may receive technical requests (such
        as map tile, API, leaderboard, or purchase calls) needed to show data or complete a
        transaction. Their handling of information is governed by their own privacy policies. We
        choose providers that help us deliver the App experience and do not use them to build
        marketing profiles on our behalf.
      </p>

      <p>
        <strong>8. Analytics and Diagnostics</strong><br />
        We may receive aggregated or anonymised diagnostic information (such as crash reports or
        general device and App version details) to fix bugs and improve performance. This information
        is not used to identify you personally. Where an App uses advertising, ad networks may also
        collect usage data as described in Section 6.
      </p>

      <p>
        <strong>9. Children&apos;s Privacy</strong><br />
        Our Apps are not directed at children under 13, and we do not knowingly collect personal
        information from children. If you believe a child has provided us with personal information,
        please contact us and we will take steps to delete it.
      </p>

      <p>
        <strong>10. Your Choices</strong><br />
        You can control App permissions (location, notifications, and on Android, display over other
        apps) in your device&apos;s Settings at any time. On iPhone and iPad, you can turn{' '}
        <strong>Sync with iCloud</strong> on or off under Settings → Sync in SG Bus &amp; MRT.         In
        Snack Snake, you can open <strong>Privacy choices</strong> in game settings when consent
        options are required, deny or revoke notification permission to stop energy-full reminders,
        and manage Game Center, Play Games, iCloud, or Google account sign-in through your device or
        platform settings. You can stop journey tracking by ending the
        trip or closing the App. You can remove home screen widgets without uninstalling the App. You
        can delete locally stored data by removing the App or clearing its storage. Because we do not
        operate Dreamcode accounts, we cannot delete data held only in your Apple iCloud, Google
        backup, Play Games, or Game Center — manage those through your Apple or Google account
        settings.
      </p>

      <p>
        <strong>11. Changes to This Policy</strong><br />
        We may update this Privacy Policy from time to time. We will post the revised policy on this
        page and update the &ldquo;Last updated&rdquo; date above. Continued use of an App after
        changes are posted means you accept the updated policy.
      </p>

      <p>
        <strong>12. Contact Us</strong><br />
        If you have questions about this Privacy Policy or our data practices, contact us via our{' '}
        <Link to="/contact">Contact</Link> page or at{' '}
        <a href="mailto:dreamcode.app@gmail.com">dreamcode.app@gmail.com</a>
        .
      </p>
    </div>
  );
}

export default Privacy;
